/**
 * Voice Agent Desk call state (module voice_desk), shared by the Inbox and the
 * app shell.
 *
 * A plain store rather than a React context: the Inbox renders the call as an
 * interaction, VoiceDeskController (mounted once in the app shell) feeds it
 * the agent stream and rings, and the LiveKit room lives here so moving
 * between chats or pages never drops a call.
 *
 *   waiting   handoffs from the AI that no agent has answered yet
 *   call      {handoff, state: connecting|live} for the call this agent is on
 *
 * Remote audio plays from elements in a container on document.body, outside
 * React, so a re-render can never unmount (and so pause) them mid-call.
 */
import { useSyncExternalStore } from 'react';
import { Room, RoomEvent, Track } from 'livekit-client';

import voiceDeskApi from '../api/voiceDesk';

const initial = {
  waiting: [],
  dismissed: [],          // handoff ids this agent chose not to answer
  answering: null,        // handoff id being accepted
  call: null,
  muted: false,
  callerAudio: false,     // the caller's microphone track is subscribed
  callerSpeaking: false,
  audioBlocked: false,    // the browser refused to start playback
};

let state = initial;
const listeners = new Set();
let notify = () => {};    // (key, type) -> shows a translated toast; set by the controller
let room = null;
let sink = null;
let ending = false;
let refreshing = false;

const set = (patch) => {
  state = { ...state, ...patch };
  listeners.forEach((l) => l());
};

const subscribe = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export const useVoiceDesk = () => useSyncExternalStore(subscribe, () => state);
export const voiceDeskState = () => state;
export const configureVoiceDesk = ({ onNotify }) => { notify = onNotify || notify; };

/** The handoff (waiting or on call) for an Inbox conversation, if any. */
export const handoffForConversation = (s, conversationId) => {
  if (!conversationId) return null;
  if (s.call && s.call.handoff.conversation_id === conversationId) return s.call.handoff;
  return s.waiting.find((h) => h.conversation_id === conversationId) || null;
};

export const ringingHandoffs = (s) => (s.call ? [] : s.waiting.filter((h) => !s.dismissed.includes(h.handoff_id)));

const teardown = () => {
  const current = room;
  room = null;
  if (current) current.disconnect();
  if (sink) { sink.remove(); sink = null; }
  set({ call: null, muted: false, callerAudio: false, callerSpeaking: false, audioBlocked: false });
};

export const hangUp = async ({ callerLeft = false } = {}) => {
  if (!state.call || ending) return;
  ending = true;
  try {
    await voiceDeskApi.hangUp(state.call.handoff.handoff_id);
  } catch { /* already ended on the server (the caller hung up first) */ }
  teardown();
  ending = false;
  notify(callerLeft ? 'voice.desk_caller_left' : 'voice.desk_call_ended', 'success');
};

const join = async (handoff) => {
  if (room) return;
  set({ call: { handoff, state: 'connecting' } });
  // voiceIsolation off: on macOS it switches the microphone to system voice processing, which can duck what the
  // browser plays. Agents sit at a desk, usually on a headset.
  const current = new Room({ audioCaptureDefaults: { echoCancellation: true, noiseSuppression: true, autoGainControl: true, voiceIsolation: false } });
  room = current;
  sink = document.createElement('div');
  sink.hidden = true;
  document.body.appendChild(sink);
  const isCaller = (p) => Boolean(p) && p !== current.localParticipant && !p.identity.startsWith('agent:') && !p.isAgent;
  const syncCallerAudio = () => set({
    callerAudio: Array.from(current.remoteParticipants.values())
      .some((p) => isCaller(p) && Array.from(p.audioTrackPublications.values()).some((pub) => pub.isSubscribed && pub.track)),
  });
  current.on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
    if (track.kind !== Track.Kind.Audio || !sink) return;
    sink.appendChild(track.attach());
    console.info('[VoiceDesk] playing audio from', participant?.identity);   // eslint-disable-line no-console
    syncCallerAudio();
  });
  current.on(RoomEvent.TrackUnsubscribed, (track) => {
    track.detach().forEach((el) => el.remove());
    syncCallerAudio();
  });
  current.on(RoomEvent.ActiveSpeakersChanged, (speakers) => set({ callerSpeaking: speakers.some(isCaller) }));
  current.on(RoomEvent.AudioPlaybackStatusChanged, () => set({ audioBlocked: !current.canPlaybackAudio }));
  current.on(RoomEvent.ParticipantDisconnected, () => {
    // The AI leaving is expected; the call is over once the caller is gone too.
    if (!Array.from(current.remoteParticipants.values()).some(isCaller)) hangUp({ callerLeft: true });
  });
  current.on(RoomEvent.Disconnected, () => {
    if (room === current && !ending) {
      teardown();
      notify('voice.desk_call_ended', 'success');
    }
  });
  try {
    await current.connect(handoff.join.url, handoff.join.token);
    await current.startAudio().catch(() => {});
    set({ audioBlocked: !current.canPlaybackAudio });
    syncCallerAudio();
    await current.localParticipant.setMicrophoneEnabled(true);
    set({ call: { handoff, state: 'live' } });
  } catch (err) {
    teardown();
    notify(err?.name === 'NotAllowedError' ? 'voice.mic_permission' : 'voice.desk_join_failed', 'error');
  }
};

const dropWaiting = (id) => set({ waiting: state.waiting.filter((h) => h.handoff_id !== id) });

/** Answer a waiting call. Resolves true when this agent is now on it. */
export const answer = async (handoff) => {
  if (state.call || state.answering) return false;
  set({ answering: handoff.handoff_id });
  try {
    const { data } = await voiceDeskApi.accept(handoff.handoff_id);
    dropWaiting(handoff.handoff_id);
    await join(data);
    return Boolean(state.call);
  } catch (err) {
    const code = err?.response?.data?.code;
    dropWaiting(handoff.handoff_id);
    notify(code === 'handoff_taken' ? 'voice.desk_taken'
      : code === 'handoff_closed' ? 'voice.desk_gone'
        : code === 'livekit_not_configured' ? 'voice.test_call_needs_livekit' : 'voice.desk_join_failed', 'error');
    return false;
  } finally {
    set({ answering: null });
  }
};

export const dismiss = (id) => set({ dismissed: [...state.dismissed, id] });

export const toggleMute = async () => {
  if (!room) return;
  await room.localParticipant.setMicrophoneEnabled(state.muted);
  set({ muted: !state.muted });
};

export const enableAudio = () => room?.startAudio().then(() => set({ audioBlocked: !room?.canPlaybackAudio })).catch(() => {});

/** What is waiting now, and a call this agent had taken before the page reloaded. */
export const refresh = async () => {
  if (refreshing) return;
  refreshing = true;
  try {
    const { data } = await voiceDeskApi.getHandoffs();
    const rows = Array.isArray(data) ? data : [];
    set({ waiting: rows.filter((h) => h.status === 'waiting') });
    const mine = rows.find((h) => h.status === 'accepted');
    if (mine && !state.call) {
      const { data: again } = await voiceDeskApi.rejoin(mine.handoff_id);
      await join(again);
    }
  } catch { /* the stream will bring new calls */ } finally {
    refreshing = false;
  }
};

/** An event from the agents' stream. Returns the new waiting handoff, if this event brought one. */
export const onAgentEvent = (data) => {
  if (data.type === 'voice_handoff') {
    if (state.waiting.some((h) => h.handoff_id === data.handoff_id)) return null;
    set({ waiting: [...state.waiting, data] });
    return data;
  }
  if (data.type === 'voice_handoff_closed') dropWaiting(data.handoff_id);
  else if (data.type === 'voice_handoff_ended' && state.call?.handoff.handoff_id === data.handoff_id) {
    teardown();
    notify('voice.desk_call_ended', 'success');
  }
  return null;
};

export const leaveDesk = () => {
  if (room) teardown();
  set({ ...initial });
};
